import React from 'react';
import { useCurrentFrame, useVideoConfig, interpolate, spring } from 'remotion';
import { COLORS } from '../constants';

interface PrixiaLogoProps {
  startFrame?: number;
  scale?: number;
  showWordmark?: boolean;
  variant?: 'full' | 'icon' | 'wordmark';
}

/**
 * Logo animado de Prixia con los 3 paralelogramos que aparecen en secuencia.
 */
export const PrixiaLogo: React.FC<PrixiaLogoProps> = ({
  startFrame = 0,
  scale = 1,
  showWordmark = true,
  variant = 'full',
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const f = frame - startFrame;

  if (f < 0) return null;

  // Cada paralelogramo aparece con stagger
  const parallelograms = [
    { points: '8,4 36,4 28,60 0,60', fill: COLORS.brandIndigo, opacity: 0.9, delay: 0 },
    { points: '18,8 44,8 38,56 12,56', fill: COLORS.brandTeal, opacity: 0.65, delay: 6 },
    { points: '28,12 52,12 48,52 24,52', fill: COLORS.brandAmber, opacity: 0.45, delay: 12 },
  ];

  // Wordmark fade
  const wordmarkOpacity = interpolate(f, [25, 40], [0, 1], {
    extrapolateRight: 'clamp',
    extrapolateLeft: 'clamp',
  });
  const wordmarkX = interpolate(f, [25, 40], [20, 0], {
    extrapolateRight: 'clamp',
    extrapolateLeft: 'clamp',
  });

  const iconSize = 64 * scale;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16 * scale,
      }}
    >
      {/* Isotipo — 3 paralelogramos */}
      {variant !== 'wordmark' && (
        <svg width={iconSize} height={iconSize} viewBox="0 0 64 64" style={{ overflow: 'visible' }}>
          {parallelograms.map((p, i) => {
            const pf = f - p.delay;
            const pScale = spring({
              frame: Math.max(0, pf),
              fps,
              config: { damping: 12, stiffness: 120, mass: 0.5 },
            });
            const pOpacity = interpolate(pf, [0, 10], [0, p.opacity], {
              extrapolateRight: 'clamp',
              extrapolateLeft: 'clamp',
            });
            // Slide from left
            const pX = interpolate(pf, [0, 12], [-20, 0], {
              extrapolateRight: 'clamp',
              extrapolateLeft: 'clamp',
            });

            return (
              <polygon
                key={i}
                points={p.points}
                fill={p.fill}
                opacity={pOpacity}
                transform={`translate(${pX}, 0) scale(${pScale})`}
                style={{
                  transformOrigin: '32px 32px',
                  filter: `drop-shadow(0 0 ${8 * scale}px ${p.fill}40)`,
                }}
              />
            );
          })}
        </svg>
      )}

      {/* Wordmark */}
      {showWordmark && variant !== 'icon' && (
        <div
          style={{
            fontSize: 38 * scale,
            fontWeight: 500,
            fontFamily: 'Inter, SF Pro Display, -apple-system, sans-serif',
            color: '#0F172A',
            letterSpacing: 1.5 * scale,
            opacity: wordmarkOpacity,
            transform: `translateX(${wordmarkX}px)`,
          }}
        >
          Prixia
        </div>
      )}
    </div>
  );
};

/**
 * Logo grande para escenas de intro/outro con glow effect.
 */
export const PrixiaLogoBig: React.FC<{
  startFrame?: number;
  scale?: number;
}> = ({ startFrame = 0, scale = 3 }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const f = frame - startFrame;

  if (f < 0) return null;

  // Glow que pulsa
  const glowIntensity = interpolate(f, [15, 35], [0, 1], {
    extrapolateRight: 'clamp',
    extrapolateLeft: 'clamp',
  });
  const glowPulse = 1 + Math.sin(f * 0.05) * 0.1;

  const containerScale = spring({
    frame: f,
    fps,
    config: { damping: 12, stiffness: 60, mass: 1 },
  });

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 24,
        transform: `scale(${containerScale})`,
      }}
    >
      {/* Glow behind icon */}
      <div style={{ position: 'relative' }}>
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: 120 * scale,
            height: 120 * scale,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${COLORS.brandIndigo}30, ${COLORS.brandTeal}15, transparent)`,
            transform: `translate(-50%, -50%) scale(${glowPulse})`,
            filter: `blur(${20 * scale}px)`,
            opacity: glowIntensity,
          }}
        />
        <PrixiaLogo startFrame={startFrame} scale={scale} showWordmark={false} variant="icon" />
      </div>

      {/* Wordmark below */}
      <div
        style={{
          opacity: interpolate(f, [30, 45], [0, 1], {
            extrapolateRight: 'clamp',
            extrapolateLeft: 'clamp',
          }),
          transform: `translateY(${interpolate(f, [30, 45], [15, 0], {
            extrapolateRight: 'clamp',
            extrapolateLeft: 'clamp',
          })}px)`,
        }}
      >
        <div
          style={{
            fontSize: 96,
            fontWeight: 500,
            fontFamily: 'Inter, SF Pro Display, -apple-system, sans-serif',
            color: '#0F172A',
            letterSpacing: 4,
          }}
        >
          Prixia
        </div>
      </div>
    </div>
  );
};
